import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import SwaggerUIBundle from 'swagger-ui-dist/swagger-ui-bundle';

interface OpenApiServer {
  url: string;
  description?: string;
}

interface OpenApiDocument {
  servers?: OpenApiServer[];
  [key: string]: unknown;
}

@Component({
  selector: 'app-api-reference',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="dp-api-reference">
      @if (isLoading) {
        <div class="dp-api-reference-state">Loading API reference</div>
      }

      @if (loadError) {
        <div class="dp-api-reference-error">
          <strong>API reference unavailable</strong>
          <span>{{ loadError }}</span>
        </div>
      }

      <div #swaggerHost class="dp-api-reference-host" [class.is-hidden]="loadError"></div>
    </div>
  `,
  styleUrl: './api-reference.component.css',
  encapsulation: ViewEncapsulation.None
})
export class ApiReferenceComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) specUrl = '';
  @Input() currentServerUrl = '';
  @Input() enableTryItOut = false;
  @ViewChild('swaggerHost', { static: true }) private swaggerHost?: ElementRef<HTMLElement>;

  isLoading = false;
  loadError = '';

  private abortController?: AbortController;
  private renderQueued = false;
  private hasView = false;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    this.hasView = true;
    this.scheduleRender();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasView) return;
    if (changes['specUrl'] || changes['currentServerUrl'] || changes['enableTryItOut']) {
      this.scheduleRender();
    }
  }

  ngOnDestroy(): void {
    this.abortController?.abort();
    this.swaggerHost?.nativeElement.replaceChildren();
  }

  private scheduleRender(): void {
    if (this.renderQueued) return;
    this.renderQueued = true;
    queueMicrotask(() => {
      this.renderQueued = false;
      this.renderReference();
    });
  }

  private renderReference(): void {
    if (!isPlatformBrowser(this.platformId) || !this.swaggerHost?.nativeElement || !this.specUrl) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    const host = this.swaggerHost.nativeElement;
    host.replaceChildren();
    this.isLoading = true;
    this.loadError = '';

    this.ngZone.runOutsideAngular(async () => {
      try {
        const response = await fetch(this.specUrl, {
          headers: { Accept: 'application/json' },
          signal: this.abortController?.signal
        });
        if (!response.ok) {
          throw new Error(`OpenAPI request failed with HTTP ${response.status}`);
        }

        const spec = await response.json() as OpenApiDocument;
        this.preferCurrentServer(spec);
        const supportedSubmitMethods = this.enableTryItOut
          ? ['get', 'post', 'put', 'patch', 'delete']
          : [];

        SwaggerUIBundle({
          spec,
          domNode: host,
          deepLinking: true,
          displayRequestDuration: true,
          docExpansion: 'list',
          filter: true,
          persistAuthorization: false,
          supportedSubmitMethods,
          tryItOutEnabled: this.enableTryItOut,
          validatorUrl: null,
          defaultModelsExpandDepth: 0
        });

        this.ngZone.run(() => {
          this.isLoading = false;
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        this.ngZone.run(() => {
          this.isLoading = false;
          this.loadError = error instanceof Error ? error.message : 'Unable to load the OpenAPI document.';
        });
      }
    });
  }

  private preferCurrentServer(spec: OpenApiDocument): void {
    const currentUrl = this.normalizeServerUrl(this.currentServerUrl);
    if (!currentUrl) return;

    const existingServers = Array.isArray(spec.servers) ? spec.servers : [];
    const otherServers = existingServers.filter(server => this.normalizeServerUrl(server.url) !== currentUrl);
    spec.servers = [
      { url: currentUrl, description: 'Current portal API' },
      ...otherServers
    ];
  }

  private normalizeServerUrl(value: string | undefined): string {
    return (value || '').trim().replace(/\/+$/, '');
  }
}
